'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import './gallery.css';
import Masonry from './masonry';
import { modalShow } from './actions/modal';
import { SignedUrls } from '@/shared';
import { buildSrcSet } from '@/image-srcset';

type GalleryImage = {
  alt: string;
  signedUrls?: SignedUrls;
  thumbnail: SignedUrls;
  placeholder?: string;
  originalWidth?: number;
  originalHeight?: number;
};

function ResetZoomButton() {
  const { resetTransform, } = useControls();
  return (
    <button className="gallery-reset-zoom" onClick={() => resetTransform()} type="button" aria-label="Reset zoom">
      <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
        <g stroke="#000" strokeWidth="4">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </g>
        <g stroke="#fff" strokeWidth="3">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </g>
      </svg>
    </button>
  );
}

function GalleryViewer({ images, startIndex, onClose, preloadImage, observeThumb, }: {
  images: GalleryImage[],
  startIndex: number,
  onClose: () => void,
  preloadImage: (index: number, priority?: `low` | `high`) => void,
  observeThumb: (el: HTMLElement | null, index: number) => void,
}) {
  const [index, setIndex,] = useState(startIndex);
  const [vertical, setVertical,] = useState(false);
  const [isFullscreen, setIsFullscreen,] = useState(false);
  const [zoomed, setZoomed,] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const image = images[index];

  useEffect(() => {
    const check = () => setVertical(window.innerWidth > 600);
    check();
    window.addEventListener(`resize`, check);
    return () => window.removeEventListener(`resize`, check);
  }, []);

  // Scroll active thumb into view
  useEffect(() => {
    const container = thumbsRef.current;
    if (!container) return;
    const active = container.children[index] as HTMLElement;
    if (!active) return;
    if (vertical) {
      container.scrollTop = active.offsetTop - container.offsetHeight / 2 + active.offsetHeight / 2;
    } else {
      container.scrollLeft = active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2;
    }
  }, [index, vertical,]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = `hidden`;
    return () => { document.body.style.overflow = ``; };
  }, []);

  // Close on browser back instead of navigating away from the page.
  // All closes funnel through popstate: the X/Escape call requestClose,
  // which pops our entry, which fires popstate, which does the real close.
  useEffect(() => {
    history.pushState({ galleryViewer: true, }, ``);
    const onPopState = () => onClose();
    window.addEventListener(`popstate`, onPopState);
    return () => window.removeEventListener(`popstate`, onPopState);
  }, [onClose,]);

  const requestClose = useCallback(() => {
    if (history.state?.galleryViewer) history.back();
    else onClose();
  }, [onClose,]);

  // Fullscreen: toggle UI state directly, try native API as bonus
  const getFullscreenElement = () => document.fullscreenElement || (document as any).webkitFullscreenElement;
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      try {
        if (next) {
          const el = viewerRef.current;
          const enter = el?.requestFullscreen || (el as any)?.webkitRequestFullscreen;
          const result = enter?.call(el);
          if (result?.catch) result.catch(() => {});
        } else if (getFullscreenElement()) {
          const exit = document.exitFullscreen || (document as any).webkitExitFullscreen;
          exit?.call(document);
        }
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Sync back if native fullscreen is exited externally (Escape on desktop)
  useEffect(() => {
    const onChange = () => { if (!getFullscreenElement()) setIsFullscreen(false); };
    document.addEventListener(`fullscreenchange`, onChange);
    document.addEventListener(`webkitfullscreenchange`, onChange);
    return () => {
      document.removeEventListener(`fullscreenchange`, onChange);
      document.removeEventListener(`webkitfullscreenchange`, onChange);
    };
  }, []);

  // Preload adjacent images
  useEffect(() => {
    [index - 1, index + 1,]
      .filter(i => i >= 0 && i < images.length)
      .forEach(i => preloadImage(i));
  }, [index, images, preloadImage,]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === `Escape`) { if (isFullscreen) { setIsFullscreen(false); try { if (getFullscreenElement()) (document.exitFullscreen || (document as any).webkitExitFullscreen)?.call(document); } catch { /* ignore */ } } else requestClose(); }
      else if (e.key === `f`) toggleFullscreen();
      else if (e.key === `ArrowRight` || e.key === `ArrowDown`) setIndex((i) => Math.min(i + 1, images.length - 1));
      else if (e.key === `ArrowLeft` || e.key === `ArrowUp`) setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener(`keydown`, onKey);
    return () => document.removeEventListener(`keydown`, onKey);
  }, [images.length, onClose, toggleFullscreen, isFullscreen,]);

  // Swipe navigation on the image area
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, };
  }, []);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current || zoomed) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) setIndex((i) => Math.min(i + 1, images.length - 1));
    else setIndex((i) => Math.max(i - 1, 0));
  }, [images.length, zoomed,]);

  const goPrev = () => setIndex((i) => Math.max(i - 1, 0));
  const goNext = () => setIndex((i) => Math.min(i + 1, images.length - 1));

  return (
    <div ref={viewerRef} className={`gallery-viewer ${vertical ? `gallery-viewer-vertical` : `gallery-viewer-horizontal`}`}>
      {!isFullscreen && (
        <div
          ref={thumbsRef}
          className={`gallery-thumbs ${vertical ? `gallery-thumbs-vertical` : `gallery-thumbs-horizontal`}`}
        >
          {images.map((img, i) => (
            <div
              key={i}
              ref={(el) => observeThumb(el, i)}
              className={`gallery-thumb ${i === index ? `gallery-thumb-active` : ``}`}
              onClick={() => setIndex(i)}
            >
              <picture style={{ display: `contents`, }}>
                <source srcSet={buildSrcSet((img.thumbnail || img.signedUrls).avif)} sizes="110px" type="image/avif" />
                <source srcSet={buildSrcSet((img.thumbnail || img.signedUrls).webp)} sizes="110px" type="image/webp" />
                <source srcSet={buildSrcSet((img.thumbnail || img.signedUrls).jpeg)} sizes="110px" type="image/jpeg" />
                <img
                  src={(img.thumbnail || img.signedUrls).jpeg[0].url}
                  alt={img.alt}
                  draggable={false}
                  loading="lazy"
                  style={{
                    aspectRatio: img.originalWidth && img.originalHeight ? `${img.originalWidth}/${img.originalHeight}` : undefined,
                    ...(img.placeholder ? { backgroundImage: `url(${img.placeholder})`, backgroundSize: `cover`, } : {}),
                  }}
                />
              </picture>
            </div>
          ))}
        </div>
      )}

      <div
        className="gallery-main"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="gallery-toolbar" onPointerDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          <button className="gallery-btn" onClick={toggleFullscreen} type="button" aria-label="Toggle fullscreen">
            {isFullscreen ? (
              <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
                <g stroke="#000" strokeWidth="4">
                  <polyline points="2 8 8 8 8 2" /><polyline points="22 8 16 8 16 2" />
                  <polyline points="22 16 16 16 16 22" /><polyline points="2 16 8 16 8 22" />
                </g>
                <g stroke="#fff" strokeWidth="3">
                  <polyline points="2 8 8 8 8 2" /><polyline points="22 8 16 8 16 2" />
                  <polyline points="22 16 16 16 16 22" /><polyline points="2 16 8 16 8 22" />
                </g>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
                <g stroke="#000" strokeWidth="4">
                  <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                  <polyline points="21 15 21 21 15 21" /><polyline points="3 9 3 3 9 3" />
                </g>
                <g stroke="#fff" strokeWidth="3">
                  <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                  <polyline points="21 15 21 21 15 21" /><polyline points="3 9 3 3 9 3" />
                </g>
              </svg>
            )}
          </button>
          <button className="gallery-btn" onClick={onClose} type="button" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
              <g stroke="#000" strokeWidth="4">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </g>
              <g stroke="#fff" strokeWidth="3">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </g>
            </svg>
          </button>
        </div>

        {index > 0 && (
          <button className="gallery-nav gallery-nav-prev" onClick={goPrev} type="button" aria-label="Previous">
            <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
              <polyline points="15 18 9 12 15 6" stroke="#000" strokeWidth="4" />
              <polyline points="15 18 9 12 15 6" stroke="#fff" strokeWidth="3" />
            </svg>
          </button>
        )}
        {index < images.length - 1 && (
          <button className="gallery-nav gallery-nav-next" onClick={goNext} type="button" aria-label="Next">
            <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" width="1em" height="1em">
              <polyline points="9 6 15 12 9 18" stroke="#000" strokeWidth="4" />
              <polyline points="9 6 15 12 9 18" stroke="#fff" strokeWidth="3" />
            </svg>
          </button>
        )}

        <TransformWrapper
          key={index}
          doubleClick={{ mode: `toggle`, }}
          panning={{ disabled: !zoomed, velocityDisabled: true, }}
          wheel={{ smoothStep: 0.05, }}
          onTransformed={(_, state) => setZoomed(state.scale > 1)}
        >
          <TransformComponent
            wrapperStyle={{ width: `100%`, height: `100%`, }}
            contentStyle={{ width: `100%`, height: `100%`, display: `flex`, alignItems: `center`, justifyContent: `center`, }}
          >
            {image.signedUrls && (
              <picture style={{ display: `contents`, pointerEvents: zoomed ? `auto` : `none`, }}>
                <source srcSet={buildSrcSet(image.signedUrls.avif)} sizes="100vw" type="image/avif" />
                <source srcSet={buildSrcSet(image.signedUrls.webp)} sizes="100vw" type="image/webp" />
                <source srcSet={buildSrcSet(image.signedUrls.jpeg)} sizes="100vw" type="image/jpeg" />
                <img
                  src={image.signedUrls.jpeg[image.signedUrls.jpeg.length - 1].url}
                  alt={image.alt}
                  draggable={false}
                  className="gallery-main-image"
                  style={image.placeholder ? { backgroundImage: `url(${image.placeholder})`, backgroundSize: `cover`, } : undefined}
                />
              </picture>
            )}
          </TransformComponent>
          {zoomed && <ResetZoomButton />}
        </TransformWrapper>

        {!isFullscreen && <div className="gallery-counter">{index + 1} / {images.length}</div>}
      </div>
    </div>
  );
}

function createPreloadLink(image: GalleryImage, priority?: `low` | `high`): HTMLLinkElement | null {
  const urls = image.signedUrls;
  if (!urls) return null;
  const format = urls.avif.length ? { type: `image/avif`, urls: urls.avif, }
    : urls.webp.length ? { type: `image/webp`, urls: urls.webp, }
      : urls.jpeg.length ? { type: `image/jpeg`, urls: urls.jpeg, }
        : null;
  if (!format) return null;
  const link = document.createElement(`link`);
  link.rel = `preload`;
  link.as = `image`;
  link.type = format.type;
  link.imageSrcset = buildSrcSet(format.urls);
  link.imageSizes = `100vw`;
  if (priority) link.fetchPriority = priority;
  document.head.appendChild(link);
  return link;
}

export default function Gallery({ images, locked, }: { images: GalleryImage[], locked: boolean }) {
  const [photoIndex, setPhotoIndex,] = useState(-1);
  const closeViewer = useCallback(() => setPhotoIndex(-1), []);
  const preloadsRef = useRef<Map<number, { link: HTMLLinkElement, priority?: `low` | `high` }>>(new Map());

  useEffect(() => {
    return () => {
      preloadsRef.current.forEach(({ link, }) => link.remove());
      preloadsRef.current.clear();
    };
  }, []);

  const preloadImage = useCallback((index: number, priority?: `low` | `high`) => {
    const existing = preloadsRef.current.get(index);
    if (existing) {
      if (existing.priority === `low` && priority !== `low`) {
        existing.link.remove();
        preloadsRef.current.delete(index);
      } else {
        return;
      }
    }
    const link = createPreloadLink(images[index], priority);
    if (link) preloadsRef.current.set(index, { link, priority, });
  }, [images,]);

  const thumbCallbacks = useRef<Map<Element, () => void>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cb = thumbCallbacks.current.get(entry.target);
          if (cb) {
            cb();
            observerRef.current?.unobserve(entry.target);
            thumbCallbacks.current.delete(entry.target);
          }
        }
      });
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const pendingObserve = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (!observerRef.current) return;
    pendingObserve.current.forEach(el => observerRef.current!.observe(el));
    pendingObserve.current = [];
  });

  const observeThumb = useCallback((el: HTMLElement | null, index: number) => {
    if (!process.env.NEXT_PUBLIC_GALLERY_PRELOAD) return;
    if (!el) return;
    if (thumbCallbacks.current.has(el)) return;
    thumbCallbacks.current.set(el, () => preloadImage(index, `low`));
    if (observerRef.current) {
      observerRef.current.observe(el);
    } else {
      pendingObserve.current.push(el);
    }
  }, [preloadImage,]);

  if (!images || images.length === 0) {
    return null;
  }

  const masonrySizes = `(min-width: 1536px) 16.6vw, (min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw`;

  function renderThumb(image: GalleryImage, index: number, onClick: () => void) {
    const aspectRatio = image.originalWidth && image.originalHeight
      ? `${image.originalWidth} / ${image.originalHeight}`
      : undefined;

    return (
      <div key={index} ref={(el) => observeThumb(el, index)} onClick={onClick} className="break-inside-avoid" style={{ cursor: `pointer`, }}>
        <div
          className="gallery-image mb-px"
          style={{
            aspectRatio,
            ...(image.placeholder && { backgroundImage: `url(${image.placeholder})`, backgroundSize: `cover`, }),
          }}
        >
          <picture>
            <source srcSet={buildSrcSet((image.thumbnail || image.signedUrls).avif)} sizes={masonrySizes} type="image/avif" />
            <source srcSet={buildSrcSet((image.thumbnail || image.signedUrls).webp)} sizes={masonrySizes} type="image/webp" />
            <source srcSet={buildSrcSet((image.thumbnail || image.signedUrls).jpeg)} sizes={masonrySizes} type="image/jpeg" />
            <img
              alt={image.alt}
              src={(image.thumbnail || image.signedUrls).jpeg[0].url}
              width="100%"
              decoding="async"
              loading="lazy"
              className="block"
            />
          </picture>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <Masonry>
        {images.map((image, index) => renderThumb(image, index, () => modalShow(`locked`)))}
      </Masonry>
    );
  }

  return (
    <>
      <Masonry>
        {images.map((image, index) => renderThumb(image, index, () => {
          setPhotoIndex(index);
        }))}
      </Masonry>
      {photoIndex > -1 && (
        <GalleryViewer
          images={images}
          startIndex={photoIndex}
          onClose={() => setPhotoIndex(-1)}
          preloadImage={preloadImage}
          observeThumb={observeThumb}
        />
      )}
    </>
  );
}
